import { useEffect, useRef, useCallback, useState } from 'react'
import { useVoiceInput } from '@/hooks/use-voice-input'

type OverlayPhase = 'entering' | 'recording' | 'transcribing' | 'exiting' | 'idle'

interface VoiceOverlayProps {
  /** Whether the overlay is active */
  active: boolean
  /** Called when transcription completes with the transcribed text */
  onTranscribed: (text: string) => void
  /** Called when the overlay finishes closing */
  onClose: () => void
  /** Bounding rect of the AI button for the fly-in animation origin */
  originRect?: DOMRect | null
}

export function VoiceOverlay({ active, onTranscribed, onClose, originRect }: VoiceOverlayProps) {
  const { state, elapsed, error, startRecording, stopAndTranscribe, cancel, getAmplitude } =
    useVoiceInput()
  const [phase, setPhase] = useState<OverlayPhase>('idle')
  const sphereRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number>(0)
  const smoothAmplitudeRef = useRef(0)
  const startedRef = useRef(false)

  // Start recording when overlay becomes active
  useEffect(() => {
    if (active && !startedRef.current) {
      startedRef.current = true
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhase('entering')
      // Brief delay to show the enter animation before starting recording
      const timer = setTimeout(async () => {
        await startRecording()
        setPhase('recording')
      }, 400)
      return () => clearTimeout(timer)
    }
    if (!active) {
      startedRef.current = false
    }
  }, [active, startRecording])

  // Animate sphere based on voice amplitude
  useEffect(() => {
    if (phase !== 'recording') return

    const animate = () => {
      const raw = getAmplitude()
      // Smooth the amplitude for fluid animation
      smoothAmplitudeRef.current += (raw - smoothAmplitudeRef.current) * 0.15

      const amp = smoothAmplitudeRef.current
      const sphere = sphereRef.current
      if (sphere) {
        // Scale: 1.0 at silence, up to 1.35 at max volume
        const scale = 1 + amp * 0.35
        // Glow intensity increases with volume
        const glowSize = 40 + amp * 80
        const glowOpacity = 0.4 + amp * 0.5
        // Subtle rotation that speeds up with voice
        const time = Date.now() / 1000
        const rotateSpeed = 20 + amp * 60
        const rotate = (time * rotateSpeed) % 360

        sphere.style.transform = `scale(${scale}) rotate(${rotate}deg)`
        sphere.style.boxShadow = `
          0 0 ${glowSize}px rgba(247, 141, 120, ${glowOpacity}),
          0 0 ${glowSize * 1.5}px rgba(225, 229, 132, ${glowOpacity * 0.5}),
          inset 0 0 ${glowSize * 0.5}px rgba(247, 141, 120, ${glowOpacity * 0.3})
        `
      }
      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(rafRef.current)
  }, [phase, getAmplitude])

  // Handle stop recording (pointer up / tap)
  const handleStop = useCallback(async () => {
    if (state !== 'recording') return
    setPhase('transcribing')
    const text = await stopAndTranscribe()
    if (text) {
      setPhase('exiting')
      // Brief exit animation
      setTimeout(() => {
        onTranscribed(text)
        setPhase('idle')
        onClose()
      }, 300)
    } else {
      setPhase('exiting')
      setTimeout(() => {
        setPhase('idle')
        onClose()
      }, 300)
    }
  }, [state, stopAndTranscribe, onTranscribed, onClose])

  // Handle cancel
  const handleCancel = useCallback(() => {
    cancel()
    setPhase('exiting')
    setTimeout(() => {
      setPhase('idle')
      onClose()
    }, 300)
  }, [cancel, onClose])

  if (!active && phase === 'idle') return null

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  // Compute entry animation origin
  const originX = originRect ? originRect.left + originRect.width / 2 : window.innerWidth / 2
  const originY = originRect ? originRect.top + originRect.height / 2 : window.innerHeight - 60

  return (
    <div
      className={`voice-overlay ${phase === 'entering' ? 'voice-overlay-enter' : ''} ${phase === 'exiting' ? 'voice-overlay-exit' : ''}`}
      onClick={phase === 'recording' ? handleStop : undefined}
      role="dialog"
      aria-label="Voice recording"
    >
      {/* Sphere */}
      <div className="voice-overlay-content">
        <div
          ref={sphereRef}
          className={`voice-sphere ${phase === 'entering' ? 'voice-sphere-enter' : ''} ${phase === 'transcribing' ? 'voice-sphere-transcribing' : ''} ${phase === 'exiting' ? 'voice-sphere-exit' : ''}`}
          style={
            phase === 'entering'
              ? ({
                  '--origin-x': `${originX}px`,
                  '--origin-y': `${originY}px`,
                } as React.CSSProperties)
              : undefined
          }
        />

        {/* Status text */}
        <div className="mt-8 text-center">
          {phase === 'recording' && (
            <div className="flex flex-col items-center gap-2">
              <span className="text-sm font-medium text-white/90 tabular-nums">
                {formatTime(elapsed)}
              </span>
              <span className="text-xs text-white/60">Tap anywhere to stop</span>
            </div>
          )}
          {phase === 'transcribing' && (
            <div className="flex flex-col items-center gap-2">
              <span className="text-sm font-medium text-white/90">Transcribing...</span>
            </div>
          )}
          {error && <span className="text-sm text-red-400">{error}</span>}
        </div>
      </div>

      {/* Cancel button */}
      {(phase === 'recording' || phase === 'entering') && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleCancel()
          }}
          className="absolute bottom-16 left-1/2 -translate-x-1/2 rounded-full border border-white/20 px-6 py-2 text-sm text-white/70 backdrop-blur-sm transition-colors hover:bg-white/10 hover:text-white"
        >
          Cancel
        </button>
      )}
    </div>
  )
}
